import { ButtonContainer, IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router";

import { ContratDocumentEditDialog, ContratDocumentUploadDialog } from ".";
import { ContratFichierApi, ContratFichierGridDto, ContratFichierSearchDto } from "../../../../../api";
import { DeleteButton, DownloadButton, EditButton, SearchTablePage } from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { exportFile } from "../../../../../utils";

export interface IContratDocumentsProps {}

export const ContratDocuments: React.FunctionComponent<IContratDocumentsProps> = props => {
  const { t } = useTl();
  const api = useApiService(ContratFichierApi);
  const { id } = useParams<{ id: string }>();
  const [currentContratFichierId, setCurrentContratFichierId] = React.useState(null);
  const [uploadDialoOpen, setUploadDialoOpen] = React.useState(false);
  const [rowLoading, setRowLoading] = React.useState(null);

  const { dispatchEvent } = useEventsContext();
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idcontratFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idcontratFichier);
          await api.contratFichierDeleteContratFichier({ id: idcontratFichier });
          dispatchEvent("SEARCH_TABLE_REFRESH");
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idcontratFichier: number) => {
      try {
        setRowLoading(idcontratFichier);
        const file = await api.contratFichierDownloadContratFichier({ id: idcontratFichier });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [api, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: ContratFichierGridDto) => (
          <ButtonContainer>
            <EditButton
              minimal={true}
              onClick={() => setCurrentContratFichierId(row.idcontratFichier)}
              loading={rowLoading === row.idcontratFichier}
              disabled={!!rowLoading && rowLoading !== row.idcontratFichier}
            />
            <DownloadButton
              minimal={true}
              onClick={() => downloadFichier(row.idcontratFichier)}
              loading={rowLoading === row.idcontratFichier}
              disabled={!!rowLoading && rowLoading !== row.idcontratFichier}
            />
            <DeleteButton
              minimal={true}
              onDelete={() => deleteFichier(row.idcontratFichier)}
              loading={rowLoading === row.idcontratFichier}
              disabled={!!rowLoading && rowLoading !== row.idcontratFichier}
            />
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.NomFichier),
        fieldName: "fileName"
      },
      {
        header: () => t(ETLCodes.DateUpload),
        fieldName: "uploadDate"
      },
      {
        header: () => t(ETLCodes.UtilisateurUpload),
        fieldName: "creationUserName"
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "typeContratFichier"
      }
    ],
    [deleteFichier, downloadFichier, rowLoading, t]
  );

  const getSearchCriterias = React.useCallback(
    () => api.contratFichierGetSearchCriterias({ includeListsValues: true }),
    [api]
  );

  const apiFactory = useAbortableApiServiceFactory(ContratFichierApi);
  const lastAbortController = React.useRef<AbortController>();
  const search = React.useCallback(
    (nextSearch?: ContratFichierSearchDto) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.contratFichierBaseSearch({ ContratFichierSearchDto: { ...nextSearch, idcontrat: +id } });
    },
    [apiFactory, id]
  );

  const onClose = React.useCallback(
    (refresh?: boolean) => {
      setCurrentContratFichierId(null);
      setUploadDialoOpen(false);
      if (refresh) {
        dispatchEvent("SEARCH_TABLE_REFRESH");
      }
    },
    [dispatchEvent]
  );

  const onAdd = React.useCallback(() => {
    setUploadDialoOpen(true);
  }, []);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <>
      <SearchTablePage
        columns={columns}
        withCard={false}
        getCriteriasFunction={getSearchCriterias}
        searchFunction={search}
        onAbort={onAbort}
        sortKeys={{ uploadDate: "DESC", fileName: "ASC" }}
        addFunc={onAdd}
      />
      {uploadDialoOpen && (
        <ContratDocumentUploadDialog onClose={onClose} dialogOpen={uploadDialoOpen} idcontrat={+id} />
      )}
      {!!currentContratFichierId && (
        <ContratDocumentEditDialog onClose={onClose} idcontratFichier={currentContratFichierId} />
      )}
    </>
  );
};
